import { useTranslation } from "react-i18next";
import { FileWarning, LogOut, ScrollText, ShieldAlert, UserX } from "lucide-react";
import { useMemo } from "react";

import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import { useGetCompanyPoliciesStatsQuery } from "@/services/(my-space)/api.company-policies";
import { useGetDocumentsSummaryQuery } from "@/services/(my-space)/api.documents";
import { useMsGetMyInvitationQuery } from "@/services/(my-space)/api.offboarding";
import { PATHS } from "@/routes/paths";
import { formatDateDmy } from "@/utils/helper";
import { useYesterdayAttendanceGap } from "../../use-yesterday-attendance-gap";
import { ALERT_PRIORITY, type IBannerAlert } from "./types";


/**
 * Collects every actionable signal for the banner's alert slot and returns them
 * ranked, most urgent first. Alerts are descriptors rather than self-rendering
 * components so the slot can rank and cap them — five components each hiding
 * themselves cannot enforce "show at most two".
 *
 * Cost note: this fans out to several endpoints on dashboard mount, most of which
 * return "nothing to show" on a normal day. They are all cheap count/stat calls,
 * but this is roughly the ceiling — adding more signals should go behind a single
 * server-side aggregate rather than another query here.
 */
export const useBannerAlerts = (): IBannerAlert[] => {
  const { t, i18n } = useTranslation("my-space");
  const { isLastMFABackupCodeUsed } = useAppSelector(selectUser);

  const { hasGap, yesterday } = useYesterdayAttendanceGap();
  const { data: policiesStats } = useGetCompanyPoliciesStatsQuery();
  const { data: documentsSummary } = useGetDocumentsSummaryQuery();
  const { data: invitation } = useMsGetMyInvitationQuery();

  const policies = policiesStats?.data;
  const documents = documentsSummary?.data;
  /** "sent" is the only state awaiting the employee; "pending" means HR has not sent it yet. */
  const hasOffboardingInvitation = invitation?.data?.status === "sent";

  return useMemo(() => {
    const alerts: IBannerAlert[] = [];

    if (hasOffboardingInvitation) {
      alerts.push({
        id: "offboarding-invitation",
        tone: "danger",
        icon: LogOut,
        priority: ALERT_PRIORITY.OFFBOARDING_INVITATION,
        title: t("ui.exit_interview_invitation"),
        description: t("ui.confirm_your_exit_interview_to_continue_offboarding"),
        action: { label: t("ui.review"), to: PATHS.MY_SPACE.MS_OFFBOARDING_PATHS.ROOT },
      });
    }

    if (policies?.overdue) {
      alerts.push({
        id: "policies-overdue",
        tone: "danger",
        icon: ScrollText,
        priority: ALERT_PRIORITY.POLICIES_OVERDUE,
        title: t("ui.policies_overdue", { count: policies.overdue }),
        description: t("ui.past_the_acknowledgement_deadline_review_and_acknowledge_now"),
        action: { label: t("ui.acknowledge"), to: PATHS.MY_SPACE.MS_COMPANY_POLICIES_PATHS.ROOT },
      });
    } else if (policies?.pending) {
      alerts.push({
        id: "policies-pending",
        tone: "warning",
        icon: ScrollText,
        priority: ALERT_PRIORITY.POLICIES_PENDING,
        title: t("ui.policies_to_acknowledge", { count: policies.pending }),
        description: t("ui.awaiting_your_acknowledgement_before_the_deadline"),
        action: { label: t("ui.review"), to: PATHS.MY_SPACE.MS_COMPANY_POLICIES_PATHS.ROOT },
      });
    }

    if (documents?.expiredDocuments) {
      alerts.push({
        id: "documents-expired",
        tone: "danger",
        icon: FileWarning,
        priority: ALERT_PRIORITY.DOCUMENTS_EXPIRED,
        title: t("ui.documents_expired", { count: documents.expiredDocuments }),
        description: t("ui.upload_a_renewed_copy_to_stay_compliant"),
        action: { label: t("ui.update"), to: PATHS.MY_SPACE.MS_DOCUMENTS_PATHS.MY_DOCUMENTS },
      });
    } else if (documents?.expiringSoonDocuments) {
      alerts.push({
        id: "documents-expiring",
        tone: "warning",
        icon: FileWarning,
        priority: ALERT_PRIORITY.DOCUMENTS_EXPIRING,
        title: t("ui.documents_expiring_soon", { count: documents.expiringSoonDocuments }),
        description: t("ui.renew_before_the_expiry_date_to_avoid_a_compliance_gap"),
        action: { label: t("labels.view"), to: PATHS.MY_SPACE.MS_DOCUMENTS_PATHS.MY_DOCUMENTS },
      });
    }

    if (hasGap) {
      alerts.push({
        id: "attendance-gap",
        tone: "danger",
        icon: UserX,
        priority: ALERT_PRIORITY.ATTENDANCE_GAP,
        title: t("ui.absent_on", { date: formatDateDmy(yesterday) }),
        description: t("ui.no_attendance_or_leave_request_found_for_that_day"),
        action: { label: t("ui.apply_leave"), to: PATHS.MY_SPACE.MS_LEAVES_MANAGEMENT_PATHS.APPLY },
        secondaryAction: {
          label: t("ui.regularize"),
          to: PATHS.MY_SPACE.MS_TIME_ATTENDANCE_PATHS.ADD_REGULARIZATION,
        },
      });
    }

    if (isLastMFABackupCodeUsed) {
      alerts.push({
        id: "mfa-disabled",
        tone: "warning",
        icon: ShieldAlert,
        priority: ALERT_PRIORITY.MFA_DISABLED,
        title: t("labels.your_mfa_is_disabled"),
        description: t("ui.last_backup_code_used_re_enable_it_to_stay_protected"),
        action: { label: t("labels.enable"), to: PATHS.MY_SPACE.MS_SETTINGS_PATHS.VTAB_SECURITY_MFA },
      });
    }

    return alerts.sort((a, b) => b.priority - a.priority);
  }, [hasOffboardingInvitation, policies, documents, hasGap, yesterday, isLastMFABackupCodeUsed, t, i18n.language]);
};
